import React, { KeyboardEvent, useEffect, useRef, useState } from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { TextArea } from '@amzn/stencil-react-components/form'
import { Col, View } from '@amzn/stencil-react-components/layout'
import { Text } from '@amzn/stencil-react-components/text'

import { ChatMessage } from 'src/services/ChatService'
import { makeLinksClickable, tryFormatJson } from './ChatFormatter'

interface ChatInterfaceProps {
    messages?: ChatMessage[]
    onSendMessage?: (message: string) => void
    isLoading?: boolean
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
    messages = [],
    onSendMessage,
    isLoading = false,
}) => {
    const [inputValue, setInputValue] = useState('')
    const messagesEndRef = useRef<HTMLDivElement>(null)

    const handleSend = () => {
        if (inputValue.trim() && onSendMessage) {
            onSendMessage(inputValue.trim())
            setInputValue('')
        }
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
        }
    }

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }, [messages])

    return (
        <View style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {messages.length === 0 ? (
                <Col alignItems='center' padding='S300' flex={1} gridGap='S100'>
                    <Text fontSize='T400' color='neutral90'>
                        GHS Authoring Assistant
                    </Text>
                    <Text fontSize='T100' color='neutral70'>
                        I am here to help you in the module creation process. Ask me anything about
                        modules, items or item pools. Please note that I am an AI assistant and may
                        occasionally make mistakes or provide incomplete information. Please verify
                        important details before making critical decisions.
                    </Text>
                </Col>
            ) : (
                <Col
                    flex={1}
                    padding='S300'
                    gridGap='S100'
                    style={{
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {messages.map((msg, index) => (
                        <View
                            key={`${msg.sender}-${msg.timestamp.getTime()}-${index}`}
                            alignSelf={msg.sender === 'user' ? 'flex-end' : 'flex-start'}
                            backgroundColor={msg.sender === 'user' ? '#007bff' : '#f1f1f1'}
                            color={msg.sender === 'user' ? 'white' : 'black'}
                            borderRadius={10}
                            padding='S300'
                            margin='S100'
                            maxWidth='80%'
                        >
                            <div style={{ color: 'inherit', lineHeight: '1.5' }}>
                                {(() => {
                                    return (
                                        tryFormatJson(msg.content) ||
                                        makeLinksClickable(msg.content)
                                    )
                                })()}
                            </div>
                            <Text
                                fontSize='T50'
                                color={msg.sender === 'user' ? 'neutral10' : 'neutral70'}
                                textAlign='right'
                            >
                                {msg.timestamp.toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                })}
                            </Text>
                        </View>
                    ))}
                    <div ref={messagesEndRef} />
                </Col>
            )}

            <View padding='S200' style={{ borderTop: '1px solid #e0e0e0' }}>
                <View style={{ position: 'relative' }}>
                    <TextArea
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder='Ask me anything... (Press Enter to send)'
                        disabled={isLoading}
                        rows={3}
                        width={'350px'}
                    />
                    <Button
                        variant={ButtonVariant.Primary}
                        onClick={handleSend}
                        disabled={isLoading || !inputValue.trim()}
                        style={{
                            position: 'absolute',
                            right: '8px',
                            bottom: '0px',
                            minWidth: '60px',
                        }}
                    >
                        Send
                    </Button>
                </View>
            </View>
        </View>
    )
}
